import CrudService from "services/cruds-service";

// Billing addresses, contact and tax details of the parties printed on documents (Bill of
// Lading shipper / consignee / notify), looked up by name on the customer, global agent and
// service provider cards.

const FILENDERS_ADDRESS =
    "Mersin University Ciftlikkoy Campus Technopark, 1st Incubation Building 35B/B02, Yenisehir/Mersin, Turkiye";
const FILENDERS_DETAILS = {
    address: FILENDERS_ADDRESS,
    phone: "+90 324 503 84 33",
    email: "accounting@filenders.com",
    taxID: "3871329803",
    taxOffice: "",
};

// Long addresses are cut so the text stays inside its box on the document.
const MAX_ADDRESS_LENGTH = 150;
const CACHE_MS = 5 * 60 * 1000;

export const normalizePartyName = (value = "") =>
    String(value)
        .replace(/[ıİ]/g, "i")
        .normalize("NFD")
        .replace(/\p{M}/gu, "")
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, " ")
        .trim();

const toList = (res) =>
    Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.data) ? res.data.data : [];

const oneLine = (value) =>
    String(value || "")
        .replace(/\s*[\r\n]+\s*/g, ", ")
        .replace(/\s+/g, " ")
        .trim();

let cached = null;

// name (normalized) -> { address, phone, email, taxID, taxOffice }. Cached for a few minutes
// so printing several copies does not reload every card list.
export const fetchPartyAddressBook = () => {
    if (cached && Date.now() - cached.at < CACHE_MS) return cached.promise;

    const promise = Promise.allSettled([
        CrudService.getCustomers(),
        CrudService.getGlobalAgents(),
        CrudService.getServiceProviders(),
    ]).then((results) => {
        const book = new Map();

        results.forEach((result) => {
            if (result.status !== "fulfilled") return;

            toList(result.value).forEach((item) => {
                const card = item?.attributes || item || {};
                const key = normalizePartyName(card.name || card.companyName);
                const address =
                    oneLine(card.billingAddress) ||
                    [card.city, card.country || card.globalAgentCountry || card.serviceProviderCountry]
                        .filter(Boolean)
                        .join(", ");
                const details = {
                    address,
                    phone: oneLine(card.phone),
                    email: oneLine(card.email),
                    taxID: oneLine(card.taxID),
                    taxOffice: oneLine(card.taxOffice),
                };
                const hasAny = Object.values(details).some(Boolean);

                if (key && hasAny && !book.has(key)) book.set(key, details);
            });
        });

        return book;
    });

    cached = { at: Date.now(), promise };
    promise.catch(() => {
        cached = null;
    });

    return promise;
};

// The party block of a document:
//   NAME
//   billing address
//   TEL: ... / E-MAIL: ...
//   TAX ID: ... / TAX OFFICE: ...
// Lines whose details are not on the card are left out; a party without a card is its name.
export const withBillingAddress = (name, book) => {
    const text = String(name || "").trim();
    if (!text) return "";

    const key = normalizePartyName(text);
    const details = book?.get(key) || (key.startsWith("FILENDERS") ? FILENDERS_DETAILS : null);
    if (!details) return text;

    const lines = [text];
    const address = details.address || "";
    if (address && !key.includes(normalizePartyName(address))) {
        lines.push(address.length > MAX_ADDRESS_LENGTH ? `${address.slice(0, MAX_ADDRESS_LENGTH - 3)}...` : address);
    }

    const contact = [details.phone && `TEL: ${details.phone}`, details.email && `E-MAIL: ${details.email}`]
        .filter(Boolean)
        .join(" / ");
    if (contact) lines.push(contact);

    const tax = [details.taxID && `TAX ID: ${details.taxID}`, details.taxOffice && `TAX OFFICE: ${details.taxOffice}`]
        .filter(Boolean)
        .join(" / ");
    if (tax) lines.push(tax);

    return lines.join("\n");
};
